import {
  Children,
  isValidElement,
  useCallback,
  useState,
  type KeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactElement,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { useResizeObserver } from "@tellescope/react-hooks";

import { cn } from "../../lib/utils";
import { useTabRailContext } from "./useTabRailContext";
import { TabRailTabPart, type TabRailTabProps } from "./TabRailTab";
import { TabRailLinkTab } from "./TabRailLinkTab";
import { tabRailTabVariants } from "./styles";

/**
 * The scrolling row of tabs within a `TabRail`.
 *
 * Tabs scroll horizontally in their own region so controls placed after `TabRail.List` (add/
 * expand/close) stay pinned. A mask-image fade softens the scrollable edge, only when tabs
 * actually overflow. `auto-cols-max` (not the default `auto`): the default keyword gives implicit
 * grid columns an *infinite* growth limit, so any leftover free space in this scroll container
 * gets distributed equally across every column, inflating each tab by the same constant amount
 * regardless of its label length -- `max-content` caps each column's growth limit at its own
 * content size instead, so tabs hug their label.
 *
 * Each tab gets a genuinely fixed per-tab width, measured via its own callback ref the instant it
 * mounts or its label changes (before paint, same timing an effect would give, without one) so the
 * hover-only `pr-4` for the delete "x" shrinks the label via `truncate` instead of growing the tab.
 *
 * Double-clicking a tab with `onRename` set swaps its label for an inline, autofocused,
 * pre-selected text input. Enter/blur commits the trimmed value; Escape cancels.
 */
export interface TabRailListProps {
  /** `TabRail.Tab` elements, one per tab. Render order is both display order and each tab's grid column. */
  children: ReactNode;
}

export const TabRailList = ({ children }: TabRailListProps) => {
  const { t } = useTranslation();
  const { activeTabId, onSelectTab, variant } = useTabRailContext();

  // `TabRail.Tab` is never actually mounted (see TabRailTab.tsx) -- its props are read straight
  // off the element here, in JSX order, so each tab's grid column is exactly its authoring
  // position with no separate registration step.
  const tabs = Children.toArray(children).filter(
    (child): child is ReactElement<TabRailTabProps> => isValidElement(child) && child.type === TabRailTabPart
  );

  const { ref: scrollRef, value: hasOverflow } = useResizeObserver<boolean, HTMLDivElement>(
    (el) => el.scrollWidth > el.clientWidth + 1,
    false
  );
  // The `underline` variant draws one indicator that slides between tabs rather than a bar per tab.
  // Its geometry comes from observing whichever tab is active: the observer rebinds when the ref
  // moves to a newly selected tab. `offset*` rather than `getBoundingClientRect`, so a dialog's
  // zoom-in transform doesn't skew it.
  const { ref: activeTabRef, value: indicator } = useResizeObserver<
    { left: number; width: number } | null,
    HTMLElement
  >((el) => ({ left: el.offsetLeft, width: el.offsetWidth }), null);
  const tracksActiveTab = variant === "underline";
  // Keyed by id AND the label it was measured for -- a rename changes `label` without changing
  // `id`, and the lock-once-measured guard below must treat that as invalidated, or the tab stays
  // pinned to its pre-rename width forever (truncating a longer new label). Only tabs with
  // `onDelete` or `onRename` are pinned, since the fixed width exists for the delete "x" and the
  // rename input. Pinning anything else risks a bad measurement for no gain: inside an opening
  // dialog the zoom-in transform shrinks `getBoundingClientRect`, locking labels too narrow.
  const [tabWidths, setTabWidths] = useState<Record<string, { width: number; label: string }>>({});

  const linkTabCount = tabs.filter((tab) => tab.props.asChild).length;
  if (linkTabCount > 0 && linkTabCount < tabs.length) {
    throw new Error("TabRail tabs must be all links (asChild) or all tabs, not a mix.");
  }
  const isLinkRail = linkTabCount > 0;

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>, index: number, tabId: string) => {
    const targetIndexByKey: Record<string, number | undefined> = {
      ArrowRight: (index + 1) % tabs.length,
      ArrowLeft: (index - 1 + tabs.length) % tabs.length,
      Home: 0,
      End: tabs.length - 1,
    };
    const targetIndex = targetIndexByKey[event.key];
    if (targetIndex !== undefined) {
      event.preventDefault();
      const target = tabs[targetIndex];
      if (target) onSelectTab?.(target.props.value);
      return;
    }
    // A focusable descendant inside a `role="tab"` element (even `tabIndex={-1}`) is an axe
    // nested-interactive violation regardless of tab order, so the delete "x" is rendered as a
    // grid-cell sibling of its tab, not a DOM descendant (see the render below) -- which means
    // Delete is the keyboard path to remove the focused tab, same as browser/editor tab strips.
    if (event.key === "Delete" || event.key === "Backspace") {
      const current = tabs.find((entry) => entry.props.value === tabId);
      if (current?.props.onDelete) {
        event.preventDefault();
        current.props.onDelete();
      }
    }
  };

  // Drives both the label's `pr-4` reveal and the delete button's opacity. Can't lean on CSS
  // `group-hover`/`:focus-within` for the button the way the label still does -- the button lives
  // outside `role="tablist"`'s DOM subtree entirely (ARIA's aria-required-children forbids a
  // tablist from containing anything but `tab` elements, even through a role-less wrapper -- a
  // real interactive descendant of `role="tab"` itself is also an axe `nested-interactive`
  // violation regardless of tabIndex), so there's no shared DOM ancestor left for a CSS group
  // selector to key off. The tab and its delete button both get matching enter/leave/focus/blur
  // handlers below (not just the tab) -- they visually overlap but are DOM siblings, not
  // ancestor/descendant, so moving the pointer from one onto the other crosses a real element
  // boundary and fires a real `mouseleave`; without the button also reporting itself revealed on
  // its own `mouseenter`, that crossing clears `revealedTabId` while the pointer is still visually
  // over the (now-hidden) button.
  const [revealedTabId, setRevealedTabId] = useState<string | null>(null);

  const [editingTabId, setEditingTabId] = useState<string | null>(null);
  const [draftLabel, setDraftLabel] = useState("");

  const startEditing = (tabId: string, label: string, event: ReactMouseEvent) => {
    event.stopPropagation();
    setEditingTabId(tabId);
    setDraftLabel(label);
  };

  const commitEdit = () => {
    if (!editingTabId) return;
    const trimmed = draftLabel.trim();
    const current = tabs.find((entry) => entry.props.value === editingTabId);
    if (trimmed) current?.props.onRename?.(trimmed);
    setEditingTabId(null);
  };

  const cancelEdit = () => setEditingTabId(null);

  // A stable (empty-deps) callback ref: React only invokes it when the underlying DOM node
  // actually mounts/unmounts, not on every re-render the way a fresh inline arrow function would
  // (each keystroke re-renders this component while editing, and a non-stable ref identity gets
  // re-invoked every time, which would re-select the whole input's text after every character).
  // Since the <input> only exists in the tree while `editing` is true, this fires exactly once --
  // right when the field appears -- same timing a mount-only effect would give, without one.
  const focusAndSelectEditInput = useCallback((el: HTMLInputElement | null) => {
    el?.select();
  }, []);

  return (
    <div className={cn("relative min-w-0 flex-1 overflow-hidden", variant !== "default" && "h-full")}>
      <div
        ref={scrollRef}
        className={cn(
          "grid auto-cols-max items-center overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          variant === "default" ? "gap-1 py-1" : "h-full",
          tracksActiveTab && "relative"
        )}
        style={
          hasOverflow
            ? {
                maskImage: "linear-gradient(to right, black calc(100% - 2rem), transparent)",
                WebkitMaskImage: "linear-gradient(to right, black calc(100% - 2rem), transparent)",
              }
            : undefined
        }
      >
        {/* `display: contents` drops this element's own box (its `role="tab"` children lay out as
            direct grid items of the scroll container above, each explicitly placed into column
            `index + 1`, row 1) without dropping its ARIA ownership -- `tablist` still only ever
            owns `tab` elements, satisfying aria-required-children, while the delete buttons below
            share those same grid cells as DOM siblings rather than descendants. */}
        {/* A rail of links is navigation, not a tablist: links are reached with Tab like any
            other links, and the current page is marked with `aria-current`. */}
        {isLinkRail ? (
          <nav style={{ display: "contents" }}>
            {tabs.map((child, index) =>
              child.props.asChild ? (
                <TabRailLinkTab
                  key={child.props.value}
                  value={child.props.value}
                  index={index}
                  selected={child.props.value === activeTabId}
                  ref={tracksActiveTab && child.props.value === activeTabId ? activeTabRef : undefined}
                  variant={variant}
                  onSelectTab={onSelectTab}
                  className={child.props.className}
                >
                  {child.props.children}
                </TabRailLinkTab>
              ) : null
            )}
          </nav>
        ) : (
          <div role="tablist" style={{ display: "contents" }}>
            {tabs.map((child, index) => {
              if (child.props.asChild) return null;
              const { value: tabId, children: label, onRename } = child.props;
              const stringLabel = typeof label === "string" ? label : undefined;
              const selected = tabId === activeTabId;
              const editing = editingTabId === tabId;
              const revealed = revealedTabId === tabId;
              const pinnedLabel = onRename || child.props.onDelete ? stringLabel : undefined;
              const cachedEntry = tabWidths[tabId];
              const cachedWidth =
                cachedEntry && pinnedLabel !== undefined && cachedEntry.label === pinnedLabel
                  ? cachedEntry.width
                  : undefined;
              return (
                <div
                  key={tabId}
                  ref={(el) => {
                    if (tracksActiveTab && selected) activeTabRef(el);
                    if (!el || pinnedLabel === undefined || cachedWidth !== undefined) return;
                    const width = el.getBoundingClientRect().width;
                    setTabWidths((prev) => {
                      if (prev[tabId]?.label === pinnedLabel) return prev;
                      // Piggyback pruning of removed tabs' entries onto this state update (a
                      // measurement/remeasurement) rather than a separate effect just to react to
                      // `tabs` shrinking -- without it, a removed tab's entry leaks in state for
                      // the component's lifetime.
                      const validIds = new Set(tabs.map((entry) => entry.props.value));
                      const next: Record<string, { width: number; label: string }> = {};
                      for (const [id, entry] of Object.entries(prev)) {
                        if (validIds.has(id)) next[id] = entry;
                      }
                      next[tabId] = { width, label: pinnedLabel };
                      return next;
                    });
                  }}
                  role="tab"
                  tabIndex={selected ? 0 : -1}
                  aria-selected={selected}
                  data-slot="tab-rail-tab"
                  data-tab-id={tabId}
                  data-trackingid={child.props["data-trackingid"]}
                  data-state={selected ? "active" : "inactive"}
                  onClick={() => {
                    if (!editing) onSelectTab?.(tabId);
                  }}
                  onDoubleClick={(event) => {
                    if (onRename && stringLabel !== undefined) startEditing(tabId, stringLabel, event);
                  }}
                  onKeyDown={(event) => handleKeyDown(event, index, tabId)}
                  onMouseEnter={() => setRevealedTabId(tabId)}
                  onMouseLeave={() => setRevealedTabId((id) => (id === tabId ? null : id))}
                  onFocus={() => setRevealedTabId(tabId)}
                  onBlur={() => setRevealedTabId((id) => (id === tabId ? null : id))}
                  style={{
                    gridColumn: index + 1,
                    gridRow: 1,
                    ...(cachedWidth !== undefined ? { width: cachedWidth } : undefined),
                  }}
                  className={cn(tabRailTabVariants({ variant, selected }), child.props.className)}
                >
                  {editing ? (
                    <input
                      ref={focusAndSelectEditInput}
                      value={draftLabel}
                      onChange={(event) => setDraftLabel(event.target.value)}
                      onClick={(event) => event.stopPropagation()}
                      onDoubleClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => {
                        event.stopPropagation();
                        if (event.key === "Enter") commitEdit();
                        else if (event.key === "Escape") cancelEdit();
                      }}
                      onBlur={commitEdit}
                      autoFocus
                      className="min-w-0 flex-1 bg-transparent text-sm font-medium text-foreground outline-none"
                    />
                  ) : (
                    <span
                      className={cn(
                        "min-w-0 flex-1",
                        stringLabel === undefined ? "flex items-center gap-1" : "truncate",
                        child.props.onDelete && revealed && "pr-4"
                      )}
                    >
                      {label}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {tabs.map((child, index) => {
          const { value: tabId, children: label, onDelete } = child.props;
          if (!onDelete || typeof label !== "string" || editingTabId === tabId) return null;
          const revealed = revealedTabId === tabId;
          return (
            <button
              key={`delete-${tabId}`}
              type="button"
              aria-label={t("Remove {{label}}", { label })}
              onClick={(event) => {
                event.stopPropagation();
                onDelete();
              }}
              // This button visually overlaps its tab (same grid cell, positioned via CSS)
              // but is a separate DOM sibling, not a descendant -- moving the pointer from the
              // tab onto this button crosses a real element boundary, firing the tab's own
              // `onMouseLeave` (see above). Without these matching handlers, that leave clears
              // `revealedTabId` while the pointer is still visually over this button's screen
              // position, hiding it (and un-reserving the label's `pr-4`) right as a user tries
              // to reach it. Mirroring the same reveal tracking here keeps `revealedTabId` set
              // to this tab through that crossing.
              onMouseEnter={() => setRevealedTabId(tabId)}
              onMouseLeave={() => setRevealedTabId((id) => (id === tabId ? null : id))}
              onFocus={() => setRevealedTabId(tabId)}
              onBlur={() => setRevealedTabId((id) => (id === tabId ? null : id))}
              style={{ gridColumn: index + 1, gridRow: 1 }}
              // NOT `absolute` -- an absolutely-positioned grid item's containing block for
              // its inset properties is the grid CONTAINER's own padding box (the whole
              // scroll region), not its assigned grid AREA, once those insets are non-`auto`.
              // Left as an ordinary (non-absolutely-positioned) grid item, `justify-self-end`
              // aligns it to ITS OWN cell's right edge correctly, and the container's own
              // `items-center` vertically centers it within the row.
              //
              // `relative` (with no inset properties, so it doesn't move anywhere): CSS's
              // painting order puts ALL positioned elements above ALL static ones, regardless
              // of DOM order -- the tab itself is `position: relative`, so without this, the
              // tab's own (later-painting-by-that-rule) label span sits on top of this button
              // and intercepts its clicks even though this button is later in DOM order.
              className={cn(
                "relative mr-1.5 flex h-4 w-4 items-center justify-center justify-self-end rounded-sm opacity-0 outline-none transition-opacity hover:bg-background focus-visible:opacity-100",
                revealed && "opacity-100"
              )}
            >
              <X className="h-3 w-3" />
            </button>
          );
        })}
        {tracksActiveTab && indicator && (
          <span
            aria-hidden
            data-slot="tab-rail-indicator"
            className="pointer-events-none absolute bottom-0 left-0 h-1 rounded-full bg-foreground transition-[transform,width] duration-200 ease-in-out"
            style={{ width: indicator.width, transform: `translateX(${indicator.left}px)` }}
          />
        )}
      </div>
    </div>
  );
};
